import React from 'react';
import { Link } from 'react-router-dom';
import { Panel } from '../../components/ui/Panel';
import { PanelHeader } from '../../components/ui/PanelHeader';
import { Button } from '../../components/ui/Button';
import { MetricStat } from '../../components/admin/MetricStat';
import { MrrChart } from '../../components/admin/MrrChart';
import { customerAccounts } from '../../data/adminCustomers';
import { revenueMonths } from '../../data/adminPlatform';
import { plans } from '../../data/plans';
export function AdminRevenue() {
  const latest = revenueMonths[revenueMonths.length - 1];
  const previous = revenueMonths[revenueMonths.length - 2];
  const growth = ((latest.mrr - previous.mrr) / previous.mrr * 100).toFixed(1);
  const pastDue = customerAccounts.filter(c => c.state === 'past_due');
  const paying = customerAccounts.filter(c => c.mrr > 0);
  const arpa = (paying.reduce((sum, c) => sum + c.mrr, 0) / paying.length).toFixed(2);
  return <div className="space-y-5" data-id="element-646" data-mp="2asbrkkuwfz45">
      <header data-id="element-647" data-mp="1fqe4v3aos67u">
        <h1 className="text-xl font-semibold tracking-tight text-ink" data-id="element-648" data-mp="1fpvyhdub8bm7">
          Revenue
        </h1>
        <p className="mt-1 text-sm text-ink-muted" data-id="element-649" data-mp="1bpu29k7cgaeb">
          Where the money is, and which accounts are about to stop paying.
        </p>
      </header>

      <Panel data-id="element-650" data-mp="1pmh3hz6bxmq1">
        <div className="grid divide-y divide-line sm:grid-cols-2 sm:divide-y-0 lg:grid-cols-4 lg:divide-x" data-id="element-651" data-mp="1kpqqxltqh99d">
          <MetricStat label="MRR" value={`$${latest.mrr}`} detail={`+${growth}% on last month`} emphasis data-id="element-652" data-mp="1yp9sfvs1hmcp" />
          <MetricStat label="New this month" value={`$${latest.newMrr}`} detail="From 14 new accounts" data-id="element-653" data-mp="1ultzb9f6i44r" />
          <MetricStat label="Churned" value={`$${latest.churnedMrr}`} detail="3 cancellations" tone="warn" data-id="element-654" data-mp="23fym9hr6yvf0" />
          <MetricStat label="ARPA" value={`$${arpa}`} detail={`${paying.length} paying accounts`} data-id="element-655" data-mp="2deeg3shlsis7" />
        </div>
      </Panel>

      <div className="grid gap-5 lg:grid-cols-[1.5fr_1fr]" data-id="element-656" data-mp="2kjffxlkts8zw">
        <Panel as="section" data-id="element-657" data-mp="2phqsw56f2zmc">
          <PanelHeader title="Seven months of MRR" description="New revenue is stacked on the retained base." level={2} data-id="element-658" data-mp="2pgyxauctl1w2" />
          <div className="px-5 py-5" data-id="element-659" data-mp="2z7k4352ndcq2">
            <MrrChart months={revenueMonths} data-id="element-660" data-mp="2z8lfxbrhm38u" />
          </div>
        </Panel>

        <Panel as="section" data-id="element-661" data-mp="34523o4uryfle">
          <PanelHeader title="Revenue by plan" description="Pro carries the business; Starter carries the funnel." level={2} data-id="element-662" data-mp="347y1lt6mh49u" />
          <ul className="divide-y divide-line" data-id="element-663" data-mp="3d6t7131ueac3">
            {plans.map(plan => {
            const accounts = customerAccounts.filter(c => c.planId === plan.id && c.mrr > 0);
            const total = accounts.reduce((sum, c) => sum + c.mrr, 0);
            const share = Math.round(total / customerAccounts.reduce((sum, c) => sum + c.mrr, 0) * 100);
            return <li key={plan.id} className="px-5 py-4" data-id="element-664" data-mp="1bgcbkmp6iiph">
                  <div className="flex items-baseline justify-between gap-3" data-id="element-665" data-mp="16hrj59g653bl">
                    <span className="text-[13px] font-medium text-ink" data-id="element-666" data-mp="16foizwlaxtt5">
                      {plan.name}
                    </span>
                    <span className="font-mono text-[13px] text-ink" data-id="element-667" data-mp="1z785pqh3xpzw">
                      ${total}
                    </span>
                  </div>
                  <div className="mt-2 h-2 rounded-full bg-mist-deep" data-id="element-668" data-mp="1u5iw8f9j9kdx">
                    <div className="h-2 rounded-full bg-brand" style={{
                  width: `${share}%`
                }} data-id="element-669" data-mp="1p77kfyzogzt2" />
                  </div>
                  <p className="mt-1.5 font-mono text-[11px] text-ink-soft" data-id="element-670" data-mp="dtlvoexqd082">
                    {accounts.length} accounts · {share}% of MRR
                  </p>
                </li>;
          })}
          </ul>
        </Panel>
      </div>

      <Panel as="section" data-id="element-671" data-mp="1xmlrjl3ml25h">
        <PanelHeader title="Failed payments" description="Monitoring keeps running through dunning — we stop watching only after 14 days overdue." level={2} action={<Button variant="secondary" size="sm" data-id="element-673" data-mp="1sok8v1a434uj">
              Export dunning list
            </Button>} data-id="element-672" data-mp="22fomnxo1wjns" />
        {pastDue.length === 0 ? <p className="px-5 py-6 text-[13px] text-ink-muted" data-id="element-674" data-mp="2gd3h5gt5vhmm">
            Nothing overdue. Every invoice this cycle collected first time.
          </p> : <ul className="divide-y divide-line" data-id="element-675" data-mp="26g8jt18mjn9x">
            {pastDue.map(customer => <li key={customer.id} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-5 py-3.5" data-id="element-676" data-mp="2bejrp6fcznpi">
                <span className="min-w-0 flex-1" data-id="element-677" data-mp="100obducpx8wp">
                  <Link to={`/admin/customers/${customer.id}`} className="block truncate text-[13px] font-medium text-ink hover:underline" data-id="element-678" data-mp="10299fty8r9zb">
                    {customer.company}
                  </Link>
                  <span className="mt-1 block truncate text-xs text-critical-ink" data-id="element-679" data-mp="3n9nfb37dfqh7">
                    {customer.flag}
                  </span>
                </span>
                <span className="font-mono text-[13px] text-ink" data-id="element-680" data-mp="3j5pgktlykhoo">
                  ${customer.mrr}
                </span>
                <Button variant="secondary" size="sm" data-id="element-681" data-mp="2qj4rjzsyghoa">
                  Retry charge
                </Button>
              </li>)}
          </ul>}
      </Panel>
    </div>;
}